import React, { FC, useState } from "react";

import {
  ActionValue,
  basketElementsSelector,
  basketInternalMode,
  basketSelector,
  basketTotalCountSelector,
  ComponentAugmentKey,
  DataViewKey,
  SentryCallback,
  shouldUpdateAdvancedSearchId,
  SnackbarCallback,
  useBasketRemove,
  useBasketShare,
  useIsVisitor,
} from "@keepeek/commons";
import {
  BasketActionsLayout,
  KButtonAction,
  KDialog,
  withSuspense,
} from "@keepeek/refront-components";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { KIcon } from "../../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import logger from "../../../lib/logger-utils";
import { GetCustomerAugmentedComponent } from "../../../lib/overrides";
import { Basket } from "../../../models/configuration/components/basket";
import { currentBasketIdState } from "../../../providers/basket/atoms";
import { useConfiguration } from "../../../providers/config/hooks";
import { configSectionComponentBasketSelector } from "../../../providers/config/selectors";
import { PagesPath } from "../../App/utils";
import BasketDownloadAction from "./BasketDownloadAction";

type BasketActionsProps = {
  sentryCallback?: SentryCallback;
  snackbarCallback?: SnackbarCallback;
  triggeredAction?: (action: ActionValue) => void;
};

const BasketActions: FC<BasketActionsProps> = function ({
  sentryCallback,
  snackbarCallback,
  triggeredAction = () => {},
}) {
  const { t } = useTranslation();
  const { publicVisitor } = useIsVisitor();
  const { push, pathname } = useRouter();
  const updateAdvancedSearchId = useSetRecoilState(
    shouldUpdateAdvancedSearchId(DataViewKey.BASKET_DETAIL),
  );
  const currentBasketId = useRecoilValue(currentBasketIdState);
  const totalCount = useRecoilValue(basketTotalCountSelector(currentBasketId));
  const currentBasket = useRecoilValue(basketSelector({ id: currentBasketId }));
  const { displayBackofficeSharedBasket } = useRecoilValue(
    configSectionComponentBasketSelector,
  ) ?? {
    displayBackofficeSharedBasket: undefined,
  };
  const { share, publicShareAuthorized } = useBasketShare(
    displayBackofficeSharedBasket ?? false,
    currentBasket?.id,
    sentryCallback,
    snackbarCallback,
  );
  const internalMode = useRecoilValue(basketInternalMode);
  const { hideShareBasketAction } = useConfiguration<Basket>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_BASKET,
  ) ?? { hideShareBasketAction: undefined };

  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const { clearBasket } = useBasketRemove(currentBasket?.id, sentryCallback);
  const [openClearConfirm, setOpenClearConfirm] = useState(false);
  const currentBasketElements = useRecoilValue(basketElementsSelector(currentBasket?.id));
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };
  const handleGoToDetail = () => {
    if (currentBasket) {
      push(`${PagesPath.BASKETS}/${currentBasket.id}`);
    }
  };
  const handleMenuClose = () => {
    setMoreAnchorEl(null);
  };
  const isMenuOpen = Boolean(moreAnchorEl);
  const handleActionMenuClick = (action: ActionValue) => {
    triggeredAction(action);
    switch (action) {
      case ActionValue.DELETE_BASKET:
        break;
      case ActionValue.EDIT_BASKET:
        break;
      case ActionValue.CLEAR_BASKET:
        setOpenClearConfirm(true);
        break;
      case ActionValue.SHARE:
        if (currentBasket) {
          share();
        }
        break;
      default:
        logger.warn("Basket - handleActionMenuClick ActionValue not yet supported = ", action);
        break;
    }
  };

  const primaryActions = [
    <BasketDownloadAction basketId={currentBasket?.id} key={"download-action"} />,
  ];

  if (
    internalMode !== undefined &&
    !internalMode &&
    publicShareAuthorized &&
    !hideShareBasketAction
  ) {
    primaryActions.push(
      <KButtonAction
        key="basketShareButton"
        className="BasketActions-basketShareButton"
        onClick={() => handleActionMenuClick(ActionValue.SHARE)}
        aria-label={t("basket.action.share.label")}
        actionButtonName={ActionValue.SHARE}
        title={t("basket.action.share.label")}
        data-testid="basketShareButton"
        disabled={totalCount <= 0}
      >
        <KIcon className="fa-solid fa-share fa-xs" />
      </KButtonAction>,
    );
  }
  if (!publicVisitor) {
    primaryActions.push(
      <KButtonAction
        key="basketGoToDetailButton"
        className="BasketActions-basketGoToDetailButton"
        aria-label={t("basket.action.go-to-detail.label")}
        actionButtonName="gotToDetail"
        title={t("basket.action.go-to-detail.label")}
        data-testid="basketGoToDetailButton"
        aria-haspopup="true"
        onClick={handleGoToDetail}
      >
        <KIcon className="fa-solid fa-eye fa-xs" />
      </KButtonAction>,
    );
  }

  primaryActions.push(
    <KButtonAction
      key="basketShowMoreActionsButton"
      className="BasketActions-basketShowMoreActionsButton"
      aria-label={t("basket.action.show-more.label")}
      actionButtonName="showMore"
      title={t("basket.action.show-more.label")}
      data-testid="basketShowMoreActionsButton"
      aria-haspopup="true"
      onClick={handleMenuOpen}
    >
      <KIcon className="fa-regular fa-ellipsis-vertical fa-xs" />
    </KButtonAction>,
  );

  const secondaryActions = [
    <MenuItem
      key="basketEditButton"
      className="BasketActions-basketEditButton"
      onClick={() => {
        handleActionMenuClick(ActionValue.EDIT_BASKET);
        handleMenuClose();
      }}
      onMouseEnter={() => {}}
    >
      <ListItemIcon>
        <KIcon className={"fa-solid fa-pen"} />
      </ListItemIcon>
      <ListItemText primary={t("basket.action.edit.label")} />
    </MenuItem>,
    <MenuItem
      key="basketEmptyButton"
      className="BasketActions-basketEmptyButton"
      onClick={() => {
        handleActionMenuClick(ActionValue.CLEAR_BASKET);
        handleMenuClose();
      }}
      onMouseEnter={() => {}}
    >
      <ListItemIcon>
        <KIcon className={"fa-solid fa-trash-list"} />
      </ListItemIcon>
      <ListItemText primary={t("basket.action.clear.label")} />
    </MenuItem>,
    <MenuItem
      key="basketDeleteButton"
      className="BasketActions-basketDeleteButton"
      onClick={() => {
        handleActionMenuClick(ActionValue.DELETE_BASKET);
        handleMenuClose();
      }}
      onMouseEnter={() => {}}
    >
      <ListItemIcon>
        <KIcon className={"fa-solid fa-trash"} />
      </ListItemIcon>
      <ListItemText primary={t("basket.action.delete.label")} />
    </MenuItem>,
  ];

  return (
    <>
      <GetCustomerAugmentedComponent
        overrideKey={ComponentAugmentKey.BasketActions}
        props={{
          moreAnchorEl: moreAnchorEl,
          isMenuOpen: isMenuOpen,
          handleMenuClose: handleMenuClose,
          primaryActions: primaryActions,
          secondaryActions: secondaryActions,
          currentBasketElements: currentBasketElements,
        }}
        Node={BasketActionsLayout}
      />
      <KDialog
        open={openClearConfirm}
        actionLabel={t("basket.layout.clear.action.label")}
        cancelLabel={t("basket.layout.clear.cancel.label")}
        titleLabel={t("basket.layout.clear.action.title.label")}
        dialogContent={
          <span>{t("basket.layout.clear.ask.label", { title: currentBasket?.title })}</span>
        }
        handleClose={() => setOpenClearConfirm(false)}
        handleAction={async () => {
          setOpenClearConfirm(false);
          await clearBasket();
          if (pathname.includes(PagesPath.BASKETS)) {
            updateAdvancedSearchId((v) => v + 1);
          }
        }}
      />
    </>
  );
};

export default withSuspense(BasketActions);
